import { useMemo } from 'react';
import {
    ExternalFinancialEntity,
    FinancialCategory,
} from '../../financialEntities';
import { PageMode } from '../../types';
import { typedEntries } from '../../util/objects';
import { isNumber } from '../../util/types';
import { usePageContext } from '../pageMode';
import { getPreferredOrFirstName, useCurrentUser } from '../user';
import { orderAccountsByBalancePerInstitution } from './orderAccountsByBalanceByInstitution';
import { Account, useAccounts } from './useAccounts';

export type AccountGroup = {
    key: FinancialCategory;
    label: string;
    emptyStateText: string;
    accounts: Account[];
    total: number;
};

export const useGroupedAccounts = ({
    returnSampleDataIfEmpty,
}: {
    returnSampleDataIfEmpty: boolean;
}) => {
    const { pageMode } = usePageContext();
    const { partnerInfo } = useCurrentUser();
    const { accounts, loading } = useAccounts({
        returnSampleDataIfEmpty,
    });
    const groupedAccounts = useMemo(
        () =>
            groupAccounts({
                accounts,
                pageMode,
                partnerName:
                    partnerInfo && getPreferredOrFirstName(partnerInfo),
            }),
        [accounts, pageMode, partnerInfo]
    );

    return { groupedAccounts, loading };
};

const groupAccounts = ({
    accounts,
    pageMode,
    partnerName,
}: {
    accounts: Account[];
    pageMode: PageMode;
    partnerName?: string | null;
}): AccountGroup[] => {
    const groupsMap: Record<
        FinancialCategory,
        {
            accounts: Account[];
            total: number;
            label: string;
            emptyStateText: string;
            type: 'asset' | 'debt';
        }
    > = {
        CashAccount: {
            accounts: [],
            total: 0,
            label: 'cash accounts',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add cash accounts to share with ${partnerName}`
                    : 'Add your cash accounts',
            type: 'asset',
        },
        Investment: {
            accounts: [],
            total: 0,
            label: 'investments',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add investments to share with ${partnerName}`
                    : 'Add your investments',
            type: 'asset',
        },
        Debt: {
            accounts: [],
            total: 0,
            label: 'debt',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add debt accounts to share with ${partnerName}`
                    : 'Add your debt accounts',
            type: 'debt',
        },
        CreditCard: {
            accounts: [],
            total: 0,
            label: 'credit cards',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add credit cards to share with ${partnerName}`
                    : 'Add your credit cards',
            type: 'debt',
        },
        Retirement: {
            accounts: [],
            total: 0,
            label: 'retirement',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add retirement accounts to share with ${partnerName}`
                    : 'Add your retirement accounts',
            type: 'debt',
        },
        OtherAsset: {
            accounts: [],
            total: 0,
            label: 'other assets',
            emptyStateText:
                partnerName && pageMode === 'PUBLIC'
                    ? `Add other assets to share with ${partnerName}`
                    : 'Add your other assets',
            type: 'asset',
        },
    };

    accounts.forEach((account) => {
        groupsMap[account.category].accounts.push(account);
        groupsMap[account.category].total += account.value;
    });

    const accountGroups: AccountGroup[] = [];

    for (const [
        key,
        { accounts, total, label, emptyStateText, type },
    ] of typedEntries(groupsMap)) {
        const goals = accounts.filter((a) => a.__typename === 'Goal');
        const goalAccounts = accounts.filter(
            (a) => a.type === 'PlentyGoalAccount'
        );

        const fes = accounts.filter(
            (a) =>
                a.type !== 'PlentyGoalAccount' &&
                a.__typename === 'FinancialEntity'
        ) as ExternalFinancialEntity[];

        const sortedAccounts = [
            ...orderAccounts(goals, type),
            ...orderAccounts(goalAccounts, type),
            ...(orderAccountsByBalancePerInstitution(fes, type) as Account[]),
        ];

        accountGroups.push({
            key,
            label,
            emptyStateText,
            accounts: sortedAccounts,
            total,
        });
    }

    return accountGroups;
};

const orderAccounts = (accounts: Account[], type: 'asset' | 'debt') =>
    accounts.sort((fe1, fe2) => {
        const a1 = type === 'asset' ? fe1 : fe2;
        const a2 = type === 'asset' ? fe2 : fe1;

        if (isNumber(a1.value) && isNumber(a2.value)) {
            return a1.value - a2.value;
        }
        if (isNumber(a1.value)) return 1;
        if (isNumber(a2.value)) return -1;
        return a1.createdAt > a2.createdAt ? 1 : -1;
    });
